// src/pages/admin/LaboratoriesPage.jsx - Updated to support edit mode
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Plus, Search, Filter, Edit, Users, MapPin, Building, MessageSquare, Wallet } from 'lucide-react';
import { useQuery } from '@apollo/client';
import { GET_LABORATORIES, GET_USERS_BY_TYPE } from '../../api/laboratory-api';
import LaboratoryForm from '../../components/laboratory/forms/LaboratoryForm';
import CombinedLabUserForm from '../../components/laboratory/forms/CombinedLabUserForm';
import ConfirmDeleteModal from '../../components/laboratory/modals/ConfirmDeleteModal';

const LaboratoriesPage = () => {
  const navigate = useNavigate();
  
  // State management
  const [selectedLaboratory, setSelectedLaboratory] = useState(null);
  const [isLabFormOpen, setIsLabFormOpen] = useState(false);
  const [isCombinedFormOpen, setIsCombinedFormOpen] = useState(false);
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [formMode, setFormMode] = useState('create'); // 'create' or 'edit'

  // Fetch data
  const {
    loading: laboratoriesLoading,
    error: laboratoriesError,
    data: laboratoriesData,
    refetch: refetchLaboratories
  } = useQuery(GET_LABORATORIES);

  const {
    loading: usersLoading,
    error: usersError,
    data: usersData,
    refetch: refetchUsers
  } = useQuery(GET_USERS_BY_TYPE, {
    variables: { userType: "LABORATORY" }
  });

  // Filter laboratories based on search term
  const filteredLaboratories = (laboratoriesData?.laboratories || []).filter(lab => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      lab.nom?.toLowerCase().includes(searchLower) ||
      lab.raisonSociale?.toLowerCase().includes(searchLower) ||
      lab.adresse?.toLowerCase().includes(searchLower) ||
      lab.ice?.toLowerCase().includes(searchLower) ||
      lab.owner?.firstName?.toLowerCase().includes(searchLower) ||
      lab.owner?.lastName?.toLowerCase().includes(searchLower) ||
      lab.owner?.email?.toLowerCase().includes(searchLower)
    );
  });

  // Event handlers
  const handleCreateNew = () => {
    setSelectedLaboratory(null);
    setFormMode('create');
    setIsCombinedFormOpen(true);
  };

  const handleViewDetails = (laboratory) => {
    navigate(`/admin/laboratories/${laboratory.id}`);
  };

  const handleEdit = (laboratory) => {
    setSelectedLaboratory(laboratory);
    setFormMode('edit');
    setIsCombinedFormOpen(true); // Use combined form for editing
  };

  const handleDelete = (laboratory) => {
    setSelectedLaboratory(laboratory);
    setIsConfirmDeleteOpen(true);
  };

  const closeAllModals = () => {
    setIsLabFormOpen(false);
    setIsCombinedFormOpen(false);
    setIsConfirmDeleteOpen(false);
    setSelectedLaboratory(null);
    setFormMode('create');
  };

  const handleSuccess = () => {
    closeAllModals();
    refetchLaboratories();
  };

  // Format display values
  const formatOwnerName = (owner) => {
    if (!owner) return 'Aucun propriétaire';
    return `${owner.firstName || ''} ${owner.lastName || ''}`.trim() || owner.email;
  };

  const formatStaffCount = (laboratory) => {
    const ownerCount = laboratory.owner ? 1 : 0;
    const staffCount = laboratory.staff?.length || 0;
    return ownerCount + staffCount;
  };

  const formatAddress = (address) => {
    if (!address) return 'Non renseignée';
    return address.length > 50 ? `${address.substring(0, 50)}...` : address;
  };

  if (laboratoriesError) {
    return (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
        <strong className="font-bold">Erreur!</strong>
        <span className="block sm:inline"> Impossible de charger les laboratoires: {laboratoriesError.message}</span>
      </div>
    );
  }

  return (
    <div className="pb-8">
      {/* Page header */}
      <div className="mb-6 bg-gradient-to-r from-green-700 to-emerald-600 text-white p-4 sm:p-6 rounded-lg shadow-lg">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center min-w-0">
            <Building2 className="w-6 h-6 sm:w-8 sm:h-8 mr-2 sm:mr-3 flex-shrink-0" />
            <div className="min-w-0">
              <h1 className="text-xl sm:text-2xl font-bold">Laboratoires</h1>
              <p className="text-green-100 text-sm hidden sm:block">
                Gérez les laboratoires partenaires de la plateforme
              </p>
            </div>
          </div>
          <button
            onClick={handleCreateNew}
            className="flex items-center px-3 py-2 sm:px-4 bg-white text-green-700 rounded-md hover:bg-green-50 transition shadow-sm text-sm flex-shrink-0"
          >
            <Plus size={16} className="mr-1 sm:mr-2" />
            <span className="hidden sm:inline">Nouveau laboratoire</span>
            <span className="sm:hidden">Nouveau</span>
          </button>
        </div>
      </div>

      {/* Search and filters */}
      <div className="mb-6 bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-4">
          <div className="flex items-center space-x-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Rechercher par nom, raison sociale, adresse, ICE, propriétaire..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              <Filter size={16} className="mr-2" />
              Filtres
            </button>
          </div>
          
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Statut
                  </label>
                  <select className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-transparent">
                    <option value="">Tous</option>
                    <option value="active">Actifs</option>
                    <option value="inactive">Inactifs</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nombre de produits
                  </label>
                  <select className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-transparent">
                    <option value="">Tous</option>
                    <option value="0">Aucun produit</option>
                    <option value="1-10">1-10 produits</option>
                    <option value="11+">11+ produits</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Trier par
                  </label>
                  <select className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-transparent">
                    <option value="name">Nom</option>
                    <option value="created">Date de création</option>
                    <option value="products">Nombre de produits</option>
                  </select>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Statistics cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-6 mb-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <Building2 className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Laboratoires</p>
              <p className="text-2xl font-bold text-gray-900">
                {filteredLaboratories.length}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Users className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Utilisateurs</p>
              <p className="text-2xl font-bold text-gray-900">
                {filteredLaboratories.reduce((acc, lab) => acc + formatStaffCount(lab), 0)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Building className="h-6 w-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Avec Raison Sociale</p>
              <p className="text-2xl font-bold text-gray-900">
                {filteredLaboratories.filter(lab => lab.raisonSociale).length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <MapPin className="h-6 w-6 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Avec Adresse</p>
              <p className="text-2xl font-bold text-gray-900">
                {filteredLaboratories.filter(lab => lab.adresse).length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Laboratories table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            Liste des laboratoires ({filteredLaboratories.length})
          </h3>
        </div>
        
        {laboratoriesLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
            <span className="ml-2 text-gray-600">Chargement...</span>
          </div>
        ) : (
          <>
          {/* Mobile card view */}
          <div className="sm:hidden divide-y divide-gray-200">
            {filteredLaboratories.length === 0 ? (
              <div className="text-center py-10 text-gray-400 text-sm">Aucun laboratoire</div>
            ) : filteredLaboratories.map((lab) => {
              const solde = lab.soldeRestant ?? 0;
              const color = solde < 0 ? 'text-red-600' : solde === 0 ? 'text-amber-600' : 'text-green-700';
              const bg    = solde < 0 ? 'bg-red-50'   : solde === 0 ? 'bg-amber-50'   : 'bg-green-50';
              const fmtN  = (n) => new Intl.NumberFormat('fr-MA', { style: 'currency', currency: 'MAD', maximumFractionDigits: 0 }).format(n);
              return (
                <div key={lab.id} className="p-4 hover:bg-gray-50 cursor-pointer" onClick={() => handleViewDetails(lab)}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-semibold text-gray-900 truncate">{lab.nom}</p>
                      {lab.raisonSociale && <p className="text-xs text-gray-500 truncate">{lab.raisonSociale}</p>}
                      {lab.owner && <p className="text-xs text-gray-400 mt-0.5">{formatOwnerName(lab.owner)}</p>}
                    </div>
                    <span className={`flex-shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-semibold ${color} ${bg}`}>
                      <Wallet size={11} />{fmtN(solde)}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                    <span>{lab.products?.length || 0} produits</span>
                    <span>·</span>
                    <span>{formatStaffCount(lab)}</span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Desktop table view */}
          <div className="hidden sm:block overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Laboratoire
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Raison Sociale
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Adresse
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ICE
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Propriétaire
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Équipe
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Produits
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Dotation restante
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredLaboratories.map((laboratory) => (
                  <tr key={laboratory.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => handleViewDetails(laboratory)}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {laboratory.nom}
                        </div>
                        <div className="text-sm text-gray-500">
                          ID: {laboratory.id.substring(0, 8)}...
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {laboratory.raisonSociale || '-'}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 max-w-xs">
                        {formatAddress(laboratory.adresse)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {laboratory.ice || '-'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {formatOwnerName(laboratory.owner)}
                      </div>
                      {laboratory.owner?.email && (
                        <div className="text-sm text-gray-500">
                          {laboratory.owner.email}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Users size={16} className="text-gray-400 mr-1" />
                        <span className="text-sm text-gray-900">
                          {formatStaffCount(laboratory)}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {laboratory.products?.length || 0} produits
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {(() => {
                        const solde = laboratory.soldeRestant ?? 0;
                        const fund  = laboratory.commissionFund ?? 0;
                        const fmtN  = (n) => new Intl.NumberFormat('fr-MA', { style: 'currency', currency: 'MAD', maximumFractionDigits: 0 }).format(n);
                        const color = solde < 0 ? 'text-red-600' : solde === 0 ? 'text-amber-600' : 'text-green-700';
                        const bg    = solde < 0 ? 'bg-red-50'   : solde === 0 ? 'bg-amber-50'   : 'bg-green-50';
                        return (
                          <div>
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-sm font-semibold ${color} ${bg}`}>
                              <Wallet size={13} />
                              {fmtN(solde)}
                            </span>
                            {fund > 0 && (
                              <p className="text-xs text-gray-400 mt-0.5">dotation: {fmtN(fund)}</p>
                            )}
                          </div>
                        );
                      })()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end space-x-1">
                        <button
                          onClick={() => navigate(`/admin/messages?userId=${laboratory.owner?.id}`)}
                          className="text-green-600 hover:text-green-900 p-1 hover:bg-green-50 rounded"
                          title="Envoyer un message"
                          disabled={!laboratory.owner?.id}
                        >
                          <MessageSquare size={16} />
                        </button>
                        <button
                          onClick={() => handleEdit(laboratory)}
                          className="text-blue-600 hover:text-blue-900 p-1 hover:bg-blue-50 rounded"
                          title="Modifier"
                        >
                          <Edit size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {filteredLaboratories.length === 0 && (
              <div className="text-center py-12">
                <Building2 className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">
                  {searchTerm ? 'Aucun laboratoire trouvé' : 'Aucun laboratoire'}
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  {searchTerm 
                    ? 'Essayez de modifier vos critères de recherche.'
                    : 'Commencez par créer un nouveau laboratoire.'
                  }
                </p>
                {!searchTerm && (
                  <div className="mt-6">
                    <button
                      onClick={handleCreateNew}
                      className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
                    >
                      <Plus className="-ml-1 mr-2 h-5 w-5" />
                      Nouveau laboratoire
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
          </>
        )}
      </div>

      {/* Modals */}
      {isLabFormOpen && (
        <LaboratoryForm
          laboratory={selectedLaboratory}
          isOpen={isLabFormOpen}
          onClose={() => setIsLabFormOpen(false)}
          onSuccess={handleSuccess}
          users={usersData?.usersByType || []}
        />
      )}

      {isCombinedFormOpen && (
        <CombinedLabUserForm
          isOpen={isCombinedFormOpen}
          onClose={() => setIsCombinedFormOpen(false)}
          onSuccess={handleSuccess}
          laboratory={selectedLaboratory}
          mode={formMode}
        />
      )}

      {isConfirmDeleteOpen && selectedLaboratory && (
        <ConfirmDeleteModal
          laboratory={selectedLaboratory}
          isOpen={isConfirmDeleteOpen}
          onClose={() => setIsConfirmDeleteOpen(false)}
          onConfirm={handleSuccess}
        />
      )}
    </div>
  );
};

export default LaboratoriesPage;